import { useEffect, useState } from 'react';
import { createPortalSession } from '../lib/api';
import { getAccessToken, isSignedIn, signIn, signOut } from '../lib/auth';

/**
 * /manage — subscription management.
 *
 * If the user is signed in (Cognito access token in sessionStorage), they
 * see a button that opens the Stripe Billing Portal. Otherwise they see a
 * "sign in with email" button that kicks off the OAuth + magic-link flow.
 *
 * The Stripe portal endpoint pulls the email from the validated access
 * token — no need to ask the user for any identifying info beyond signing in.
 */
export default function Manage() {
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check sign-in state on mount. Cheap (just reads sessionStorage), so no
  // need for any async dance — but isSignedIn() doesn't try to refresh, so
  // a stale-but-refreshable session will look signed-out until they click.
  useEffect(() => {
    setAuthed(isSignedIn());
  }, []);

  async function openPortal() {
    setError(null);
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) {
        // Refresh failed or no session — kick off a fresh sign-in.
        await signIn('/manage');
        return;
      }
      const { url } = await createPortalSession(token);
      window.location.href = url;
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  }

  function handleSignIn() {
    setError(null);
    signIn('/manage').catch((err) => setError((err as Error).message));
  }

  function handleSignOut() {
    signOut();
    setAuthed(false);
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-24">
      <h1 className="text-3xl md:text-4xl font-bold">Manage subscription</h1>
      <p className="mt-4 text-slate-300">
        Cancel auto-renewal, update your payment method, or download invoices via
        Stripe's secure billing portal.
      </p>

      {authed ? (
        <div className="mt-8 space-y-4">
          <button
            onClick={openPortal}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? 'Opening portal…' : 'Open billing portal'}
          </button>
          <button
            onClick={handleSignOut}
            className="block mx-auto text-sm text-slate-400 hover:text-white underline"
          >
            Sign out
          </button>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          <button onClick={handleSignIn} className="btn-primary w-full">
            Sign in with email
          </button>
          <p className="text-xs text-slate-500 text-center">
            We'll email you a one-click sign-in link. No passwords.
          </p>
        </div>
      )}

      {error && (
        <p className="mt-6 text-sm text-red-400">
          {error}. If this keeps happening, email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">support</a>.
        </p>
      )}

      <div className="mt-12 rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        <strong className="text-slate-300">Lost access to your email?</strong> Email{' '}
        <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">support@thedraftlion.com</a>{' '}
        from any address — we can verify ownership a different way.
      </div>
    </div>
  );
}
